import type { FC } from "react";
import { CheckBadgeIcon } from "@heroicons/react/20/solid";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

/** `s` is the directory card's original size, unchanged. `m`/`l` are the profile page's summary
 *  card and hero (spec `practitioner-profile.md` §7.7). */
const verifiedNameVariants = cva("font-semibold leading-[1.3] text-neutral-black", {
  variants: {
    size: {
      s: "text-24",
      m: "text-32",
      l: "text-44",
    },
  },
  defaultVariants: {
    size: "s",
  },
});

const BADGE_SIZE = {
  s: "size-20",
  m: "size-28",
  l: "size-28",
} as const;

type VerifiedNameProps = VariantProps<typeof verifiedNameVariants> & {
  name?: string;
  verified?: boolean;
  className?: string;
  /** Overrides the name text's colour — e.g. white on the profile hero's dark-blue band. */
  nameClassName?: string;
  as?: "h1" | "h2" | "span";
};

export const VerifiedName: FC<VerifiedNameProps> = ({ name, verified, size, className, nameClassName, as: Tag = "span" }) => {
  const resolvedSize = size ?? "s";

  return (
    <Tag className={cn("flex items-center gap-8", className)}>
      <span className={cn(verifiedNameVariants({ size: resolvedSize }), nameClassName)}>
        {name ?? "Unknown practitioner"}
      </span>
      {verified && <CheckBadgeIcon className={cn(BADGE_SIZE[resolvedSize], "shrink-0 text-brand-blue")} />}
    </Tag>
  );
};
